import React, { useState } from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import HouseService from '../../../services/HouseService';
import ImageCarousel from '../ImageCarousel';
import { ConfirmationModal, toast } from '../V2Components';
import { PLACEHOLDER_IMAGES } from '../../../constants/images';
import { uploadErrorMessage } from '../../../utils/uploadErrors';
import { useFilePicker } from '../../../hooks/useFilePicker';

// Mirror of api/houses.py MAX_HOUSE_IMAGES — keep in sync. The frontend cap
// is a UX guard (disables the add button, shows the count); the backend stays
// the source of truth and rejects over-limit uploads with a 400.
const MAX_HOUSE_IMAGES = 10;

/**
 * Photo management for the House Details page. Wraps the read-only
 * ImageCarousel with upload, set-primary, and delete controls so users can
 * curate a house's photo gallery in place.
 *
 * Expects gallery photos only — the parent filters out is_floor_plan rows
 * before passing `images` (floor plans live in their own flow).
 *
 * @param {number} houseId - House ID images belong to
 * @param {Array} images - Current gallery images (API shape: id, url, is_primary, original_filename)
 * @param {Function} onChange - Called after a successful mutation so the parent can refetch
 * @param {string} aspectRatio - Tailwind aspect class(es) for the carousel viewer
 */
const HouseImageManager = ({ houseId, images = [], onChange, aspectRatio = 'aspect-[16/10]' }) => {
    const [uploading, setUploading] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [settingPrimaryId, setSettingPrimaryId] = useState(null);

    const isFull = images.length >= MAX_HOUSE_IMAGES;
    // Any in-flight mutation. Used to disable controls so the user can't fire
    // overlapping requests (e.g. set-primary while an upload is still running)
    // that would race against each other and the onChange refetch.
    const busy = uploading || deleting || settingPrimaryId !== null;

    // Await the parent refetch, converting a refresh failure into a
    // stale-gallery warning instead of letting it masquerade as a mutation
    // failure (the mutation already succeeded on the backend by the time
    // this runs) or escape as an unhandled rejection.
    const refreshGallery = async () => {
        try {
            await onChange?.();
        } catch {
            toast.error('Saved, but the gallery failed to refresh — reload the page to see the latest photos');
        }
    };

    const handleUpload = async (files) => {
        if (!files || files.length === 0) return;
        const remaining = MAX_HOUSE_IMAGES - images.length;
        if (remaining <= 0) {
            toast.error(`A house can have at most ${MAX_HOUSE_IMAGES} photos`);
            return;
        }
        const toUpload = Array.from(files).slice(0, remaining);
        if (toUpload.length < files.length) {
            toast.error(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} can be added`);
        }

        setUploading(true);
        // Uploads are sequential, so a failure on file N still leaves files
        // 1..N-1 persisted on the backend. Track per-file success and always
        // refetch if *anything* landed — otherwise successful photos stay
        // invisible until a manual refresh.
        let succeeded = 0;
        let lastError = null;
        try {
            for (const f of toUpload) {
                try {
                    await HouseService.uploadHouseImage(houseId, f);
                    succeeded += 1;
                } catch (err) {
                    lastError = err;
                }
            }
        } finally {
            // Hold the busy state until the parent refetch settles — clearing
            // it earlier would let a second mutation start against a stale
            // gallery and race the in-flight refresh.
            try {
                if (succeeded > 0) await refreshGallery();
            } finally {
                setUploading(false);
            }
        }

        const failed = toUpload.length - succeeded;
        if (failed === 0) {
            toast.success(`${succeeded} photo${succeeded === 1 ? '' : 's'} uploaded`);
        } else if (succeeded > 0) {
            toast.error(
                `Uploaded ${succeeded} photo${succeeded === 1 ? '' : 's'}, ${failed} failed: ` +
                uploadErrorMessage(lastError, 'upload failed')
            );
        } else {
            toast.error(uploadErrorMessage(lastError, 'Failed to upload photo'));
        }
    };

    // OS-native file chooser. See hooks/useFilePicker — bare accept="image/*"
    // keeps the iOS "Take Photo / Photo Library / Choose File" sheet, so
    // users can take a new photo on mobile.
    const { triggerPicker: openImagePicker, fileInputElement: imageFileInput } = useFilePicker({
        kind: 'image',
        multiple: true,
        maxSizeMB: 25,
        onFiles: handleUpload,
        disabled: isFull || busy,
    });

    const handleSetPrimary = async (image) => {
        if (busy) return; // re-entrancy guard against rapid double-clicks
        setSettingPrimaryId(image.id);
        try {
            // Only the mutation itself belongs in this try — a refresh
            // failure must not fire the "Failed to set primary" toast after
            // the backend already applied the change.
            await HouseService.setHouseImagePrimary(houseId, image.id);
        } catch (err) {
            toast.error(err.message || 'Failed to set primary photo');
            setSettingPrimaryId(null);
            return;
        }
        toast.success('Primary photo updated');
        try {
            // Await the refetch so controls stay disabled until fresh state lands.
            await refreshGallery();
        } finally {
            setSettingPrimaryId(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!imageToDelete) return;
        setDeleting(true);
        try {
            // Only the mutation itself belongs in this try — see handleSetPrimary.
            await HouseService.deleteHouseImage(houseId, imageToDelete.id);
        } catch (err) {
            toast.error(err.message || 'Failed to delete photo');
            setDeleting(false);
            return;
        }
        toast.success('Photo deleted');
        // The delete succeeded — close the modal regardless of how the
        // refresh below fares.
        setImageToDelete(null);
        try {
            // Await the refetch so controls stay disabled until fresh state lands.
            await refreshGallery();
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Header — Add Photo lives top-right of the image canvas. */}
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--text-muted)]">
                    {images.length}/{MAX_HOUSE_IMAGES} photos
                </span>
                {imageFileInput}
                <button
                    type="button"
                    disabled={isFull || busy}
                    onClick={openImagePicker}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary)] text-[var(--primary-text)] rounded-full text-xs font-bold shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    aria-label="Add photo"
                >
                    {uploading ? (
                        <>
                            <span className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                            Uploading...
                        </>
                    ) : isFull ? (
                        'Max photos reached'
                    ) : (
                        <>
                            <Plus size={14} />
                            Add Photo
                        </>
                    )}
                </button>
            </div>

            {/* Viewer */}
            <div className="flex items-center justify-center">
                <ImageCarousel
                    images={images}
                    aspectRatio={aspectRatio}
                    className="w-full max-w-3xl"
                />
            </div>

            {/* Thumbnail strip with per-photo manage actions. Only shown when
                there are photos — an empty house leans on the carousel's own
                empty state + the Add Photo button above. */}
            {images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                    {images.map((img) => (
                        <div
                            key={img.id}
                            className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--bg-card)]"
                        >
                            <img
                                src={img.url || PLACEHOLDER_IMAGES.house}
                                alt={img.original_filename || 'House photo'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    // Fall back to the placeholder if a presigned
                                    // URL has expired / 404s. Clear the handler
                                    // first so the placeholder can't loop.
                                    if (e.currentTarget.src !== PLACEHOLDER_IMAGES.house) {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = PLACEHOLDER_IMAGES.house;
                                    }
                                }}
                            />

                            {/* Action controls are always visible (not hover-gated)
                                so they work on touch devices, which have no hover. */}
                            {img.is_primary ? (
                                <span
                                    className="absolute top-1 left-1 bg-[var(--primary)] text-[var(--primary-text)] rounded-full p-1 shadow"
                                    title="Primary photo"
                                >
                                    <Star size={11} fill="currentColor" />
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => handleSetPrimary(img)}
                                    title="Set as primary"
                                    aria-label="Set as primary"
                                    className="absolute top-1 left-1 bg-white/90 text-[var(--primary)] rounded-full p-1.5 shadow hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {settingPrimaryId === img.id ? (
                                        <span className="block animate-spin rounded-full h-[11px] w-[11px] border-b border-current" />
                                    ) : (
                                        <Star size={11} />
                                    )}
                                </button>
                            )}

                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => setImageToDelete(img)}
                                title="Delete photo"
                                aria-label="Delete photo"
                                className="absolute top-1 right-1 bg-white/90 text-[var(--error)] rounded-full p-1.5 shadow hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Trash2 size={11} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmationModal
                isOpen={!!imageToDelete}
                onClose={() => setImageToDelete(null)}
                onConfirm={handleDeleteConfirmed}
                isLoading={deleting}
                title="Delete this photo?"
                message="Are you sure you want to delete this photo? This action cannot be undone."
                confirmText={deleting ? 'Deleting...' : 'Delete'}
                isDanger
                icon={Trash2}
            />
        </div>
    );
};

export default HouseImageManager;
